import React, {useRef, useState} from 'react';
import {Button} from '~/components/Button';
import {Menu} from '~/components/Menu';
import {ISortColumn, TableSort} from '~/components/Table';
import {Title} from '~/components/Title';
import {useAbility} from '~/lib/ability';
import {CSV} from '~/lib/csv';
import {Intent} from '~/lib/intent';
import {DashboardAbility} from '~/modules/Dashboard/ability';
import {getPayerLabel} from '~/modules/Dashboard/lib/payer';
import {
    formatIsPaas,
    getVisibleFieldsWithStorage,
    IPerformanceFieldOptions,
    performanceFields,
    setVisibleFieldsWithStorage,
    toGranularityDateString
} from '~/modules/Dashboard/lib/performance';
import {IPerformance} from '~/modules/Dashboard/models/Performance';
import {useDashboardReducer} from '~/modules/Dashboard/reducer';
import {useSession} from '~/modules/Profile/session';
import style from './index.module.scss';

export const PerformanceTable = () => {
    const ability = useAbility(DashboardAbility);
    const {state: {data, table, query: {performanceType, granularity}}, setTableState} = useDashboardReducer();
    const [visibleFields, setVisibleFields] = useState<(keyof IPerformance)[]>(
        getVisibleFieldsWithStorage('table') || performanceFields.map(field => field.key));
    const {session: {user}} = useSession();

    const fields = performanceFields.filter(field =>
        field.tableCell &&
        (!field.performanceType || field.performanceType === performanceType) &&
        !(field.hideByAbility && field.hideByAbility(ability))
    );

    const toggleField = (field: IPerformanceFieldOptions) => {
        if (visibleFields.includes(field.key)) {
            setVisibleFieldsWithStorage(visibleFields.filter(visibleField => visibleField !== field.key), 'table', setVisibleFields);
        } else {
            setVisibleFieldsWithStorage(visibleFields.concat(field.key), 'table', setVisibleFields);
        }
    };

    const fieldsToRender = visibleFields.length > 0 ? fields.filter(field => visibleFields.includes(field.key)) : fields;

    const toggleFieldsRef = useRef();

    const columns: ISortColumn<IPerformance>[] = [
        {
            header: 'Time (UTC)',
            field: 'timestamp',
            cell: performance => toGranularityDateString(new Date(performance.timestamp), granularity)
        }, ...fieldsToRender.map<ISortColumn<IPerformance>>(field => ({
            header: field.title,
            field: field.key,
            cell: field.tableCell,
            sort: field.sort,
            footer: field.tableFooter
        }))
    ];

    return data.length > 0 && <>
        <Title toolbar={<>
            <Button responsive
                onClick={() => setTableState({...table, short: !table.short})}
                intent={table.short ? Intent.PRIMARY : Intent.DEFAULT}
                leftIcon={<i className='fa fa-th-large'/>}
            >Short</Button>
            <Button responsive
                ref={toggleFieldsRef}
                leftIcon={<i className='fa fa-columns'/>}
            >Columns</Button>
            <Menu parentRef={toggleFieldsRef} closeOnClick={false} items={fields.map(field => {
                const isVisible = visibleFields.includes(field.key);
                return ({
                    children: field.title,
                    intent: isVisible && Intent.SUCCESS,
                    className: !isVisible && style.noVisibleField,
                    onClick: () => toggleField(field)
                });
            })
            }/>
            <Button responsive
                onClick={() => CSV.fromDataSource(data.map(field => ({
                    ...field,
                    adType: field.adType?.label,
                    country: field.country?.label,
                    platform: field.platform?.label,
                    agency: field.agency?.label,
                    seller: field.seller?.label,
                    payer: getPayerLabel(field.payer),
                    isPaas: formatIsPaas(field.isPaas)
                })), columns.map(column => column.field)).download('performance.csv')}
                leftIcon={<i className='far fa-file-excel'/>}
            >Export</Button>
        </>}>
            Performance
        </Title>
        <TableSort footer
            key='table'
            columns={columns}
            records={data}
            header/>
    </>;
};
